<script setup lang="ts">
import { AlertCircle, CheckCircle2, LoaderCircle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const props = withDefaults(defineProps<{
    status: 'idle' | 'loading' | 'success' | 'error';
    title?: string;
    description?: string;
    retryLabel?: string;
    class?: string;
}>(), {
    title: '',
    description: '',
    retryLabel: 'Try again',
    class: '',
});

defineEmits<{
    retry: [];
}>();

const icon = computed(() => {
    if (props.status === 'success') return CheckCircle2;
    if (props.status === 'error') return AlertCircle;
    return LoaderCircle;
});
</script>

<template>
    <Transition
        enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
        enter-from-class="translate-y-1 opacity-0 motion-reduce:translate-y-0"
        leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
        leave-to-class="opacity-0"
    >
        <div
            v-if="status !== 'idle'"
            :class="cn(
                'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm',
                status === 'success' && 'border-primary/20 bg-primary/5 text-foreground',
                status === 'error' && 'border-destructive/25 bg-destructive/5 text-destructive',
                status === 'loading' && 'border-border/70 bg-muted/35 text-foreground',
                props.class,
            )"
            :role="status === 'error' ? 'alert' : 'status'"
            aria-live="polite"
        >
            <span
                :class="cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
                    status === 'success' && 'bg-primary/10 text-primary',
                    status === 'error' && 'bg-destructive/10 text-destructive',
                    status === 'loading' && 'bg-muted text-muted-foreground',
                )"
            >
                <component :is="icon" :class="cn('size-4', status === 'loading' && 'animate-spin motion-reduce:animate-none')" />
            </span>
            <div class="min-w-0 flex-1">
                <p v-if="title" class="font-medium">{{ title }}</p>
                <p v-if="description" class="text-xs leading-5 opacity-75">{{ description }}</p>
            </div>
            <Button v-if="status === 'error'" type="button" variant="ghost" size="sm" @click="$emit('retry')">
                {{ retryLabel }}
            </Button>
        </div>
    </Transition>
</template>
